import './claim-entity.detail.contact.scss';
import './claim-data.scss';

import { Box, FormControlLabel, Paper, Radio, RadioGroup } from '@mui/material';
import React, { useState, useCallback, useEffect } from 'react';
// import { useClaimQuery } from '@converge/graphql';
import {
  CircularLoader,
  ConvergeDetailAccordion,
  FormCheckBoxField,
  NewFormTextField,
  NewTypeAhead,
} from '../../../presentation';
import { ClaimFormNumberInputAdornmentField } from './components/claim-form-number-input-adornment-field';
import { Controller, FormProvider } from 'react-hook-form';
import { useOutletContext } from 'react-router-dom';

import {
  useUpsertClaimDataMutation,
  useClaimDataQuery,
  useThreatActorsQuery,
  useCatCodesQuery,
} from '@converge/graphql';
import { useConvergeSnackBar } from '../../../contexts';
import { useForm } from 'react-hook-form';

import { IProps } from './claim-entity.detail.overview';

export const ClaimDataPage = () => {
  const { claimUuid } = useOutletContext<IProps>();
  const { upsertClaimData } = useUpsertClaimDataMutation();
  const { notify } = useConvergeSnackBar();

  // GraphQL Int maximum value (32-bit signed integer)
  const MAX_INT_VALUE = 2147483647;

  // Helper function to capture field changes on blur/focus change
  const handleFieldChange = useCallback(
    (fieldName: string, value: string | boolean | null | string[]) => {
      setDirtyFields((prev) => ({
        ...prev,
        [fieldName]: {
          value: value as string | boolean | null,
        },
      }));
    },
    []
  );

  const [dateFieldErrors, setDateFieldErrors] = useState<{
    rorSentDate: boolean;
    polSentDate: boolean;
  }>({
    rorSentDate: false,
    polSentDate: false,
  });
  console.log(setDateFieldErrors);

  const { data } = useThreatActorsQuery();
  const threatActorsOption = data?.threatActors.edges.map((actorItem) => ({
    label: actorItem?.node?.name,
    value: actorItem.node.uuid,
  }));

  const { data: catCodesData, loading: catCodesLoading } = useCatCodesQuery();
  const catCodeOption = catCodesData?.catCodes.edges.map((item) => ({
    label: item?.node?.name ?? '',
    value: item?.node?.uuid ?? '',
  }));

  // Helper function to prevent typing values over max limit
  const handleIntegerKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const currentValue = (e.target as HTMLInputElement).value;

      // Allow control keys
      if (
        [
          'Backspace',
          'Delete',
          'Tab',
          'Enter',
          'ArrowLeft',
          'ArrowRight',
          'ArrowUp',
          'ArrowDown',
        ].includes(e.key)
      ) {
        // On Tab or Enter, capture the current value before navigation
        if (e.key === 'Tab' || e.key === 'Enter') {
          const fieldName = (e.target as HTMLInputElement).name;
          if (fieldName) {
            handleFieldChange(fieldName, currentValue);
          }
        }
        return;
      }

      // Prevent non-numeric input (except for allowed characters)
      if (e.key === '-' || e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        return;
      }

      // Check if the new value would exceed max
      if (e.key >= '0' && e.key <= '9') {
        const newValue = currentValue + e.key;
        if (Number(newValue) > MAX_INT_VALUE) {
          e.preventDefault();
        }
      }
    },
    [handleFieldChange]
  );

  // Initialize form with react-hook-form
  const form = useForm({
    defaultValues: {
      claimVariant: '',
      cryptoWalletId: '',
      lengthOfDisruption: '',
      recordsImpacted: '',
      initialBudget: '',
      rorSentDate: '',
      polSentDate: '',
      numberOfEndpoints: '',
      typePaid: [] as string[],
      daysOccurrenceToDetection: '',
      daysDetectionToContainment: '',
      dataLoss: '',
      initialDemand: '',
      daysDetectionToForensicCompletion: '',
      dataMined: '',
      finalPayment: '',
      ftfAmountOriginalLoss: '',
      ftfAmountRecovered: '',
      becAccountsCompromised: '',
      contributionFromOtherInsurance: '',
      catCodeUuid: '',
      threatActorUuid: '',
    },
  });

  // Get current claim data
  const {
    claimData,
    loading: claimDataLoading,
    refetch: refetchClaimData,
  } = useClaimDataQuery({ claimUuid });

  // Watch all form changes and update dirty fields immediately
  useEffect(() => {
    const subscription = form.watch((value, { name, type }) => {
      if (name && (type === 'change' || !type)) {
        // Get the current value safely
        const currentValue =
          value && typeof value === 'object'
            ? value[name as keyof typeof value]
            : undefined;

        // Check if this is a number field that might have formatting differences
        const numberFields = [
          'lengthOfDisruption',
          'recordsImpacted',
          'initialBudget',
          'daysOccurrenceToDetection',
          'numberOfEndpoints',
          'daysDetectionToContainment',
          'initialDemand',
          'daysDetectionToForensicCompletion',
          'dataMined',
          'finalPayment',
          'ftfAmountOriginalLoss',
          'ftfAmountRecovered',
          'becAccountsCompromised',
          'contributionFromOtherInsurance',
        ];

        let valueToStore: string | boolean | null | string[] = Array.isArray(
          currentValue
        )
          ? (currentValue.filter(Boolean) as string[])
          : currentValue || '';

        // For number fields, normalize by removing commas and dollar signs to prevent
        // auto-save loops when "1000" vs "1,000" are treated as different values
        if (numberFields.includes(name) && typeof valueToStore === 'string') {
          // Remove formatting characters for storage
          valueToStore = valueToStore.replace(/[$,]/g, '');
        }

        // Add to dirty fields immediately when any field changes
        setDirtyFields((prev) => ({
          ...prev,
          [name]: {
            value: valueToStore,
          },
        }));
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Helper function to format number with commas for fields that use ClaimFormNumberInputAdornmentField
  const formatNumberWithCommas = (value: number | null | undefined): string => {
    if (!value) return '';
    return value.toLocaleString();
  };

  // Update form when claim data is loaded
  useEffect(() => {
    if (claimData) {
      const formValues = {
        claimVariant: claimData.claimVariant || '',
        cryptoWalletId: claimData.cryptoWalletId || '',
        lengthOfDisruption: claimData.lengthOfDisruption?.toString() || '',
        recordsImpacted: claimData.recordsImpacted?.toString() || '',
        initialBudget: formatNumberWithCommas(claimData.initialBudget),
        rorSentDate: claimData.rorSentDate || '',
        polSentDate: claimData.polSentDate || '',
        numberOfEndpoints: claimData.numberOfEndpoints?.toString() || '',
        typePaid: claimData.typePaid
          ? claimData.typePaid
              .split(',')
              .map((s: string) => s.trim())
              .filter(Boolean)
          : [],
        daysOccurrenceToDetection:
          claimData.daysOccurrenceToDetection?.toString() || '',
        daysDetectionToContainment:
          claimData.daysDetectionToContainment?.toString() || '',
        dataLoss: claimData.dataLoss || '',
        initialDemand: formatNumberWithCommas(claimData.initialDemand),
        daysDetectionToForensicCompletion:
          claimData.daysDetectionToForensicCompletion?.toString() || '',
        dataMined: claimData.dataMined?.toString() || '',
        finalPayment: formatNumberWithCommas(claimData.finalPayment),
        ftfAmountOriginalLoss: formatNumberWithCommas(
          claimData.ftfAmountOriginalLoss
        ),
        ftfAmountRecovered: formatNumberWithCommas(
          claimData.ftfAmountRecovered
        ),
        becAccountsCompromised:
          claimData.becAccountsCompromised?.toString() || '',
        contributionFromOtherInsurance: formatNumberWithCommas(
          claimData.contributionFromOtherInsurance
        ),
        catCodeUuid: claimData.catCodeUuid || '',
        threatActorUuid: claimData?.threatActor?.node?.uuid || '',
      };

      form.reset(formValues);
      // Clear dirty fields when loading fresh data from server to prevent auto-save loop
      setDirtyFields({});
    }
  }, [claimData, form]);

  // Re-apply catCodeUuid once catCodeOption arrives — the form.reset above may
  // run before the catCodes query resolves, leaving NewTypeAhead with no match.
  useEffect(() => {
    if (catCodesData && claimData?.catCodeUuid) {
      form.setValue('catCodeUuid', claimData.catCodeUuid, {
        shouldDirty: false,
      });
    }
  }, [catCodesData, claimData, form]);

  const [dirtyFields, setDirtyFields] = useState<
    Record<
      string,
      {
        value: string | boolean | null | string[];
      }
    >
  >({});

  // Function to prepare and send current dirty fields to backend
  const prepareDataForBackend = useCallback(() => {
    // Filter out CAT code fields from dirtyFields
    const filteredDirtyFields = Object.keys(dirtyFields)
      .filter((key) => key !== 'catCodeUuid')
      .reduce((obj, key) => {
        obj[key] = dirtyFields[key];
        return obj;
      }, {} as typeof dirtyFields);

    if (Object.keys(filteredDirtyFields).length === 0) {
      return null;
    }

    // Define which fields should be converted to numbers
    const numberFields = [
      'lengthOfDisruption',
      'recordsImpacted',
      'daysDetectionToContainment',
      'numberOfEndpoints',
      'daysOccurrenceToDetection',
      'daysDetectionToForensicCompletion',
      'becAccountsCompromised',
      'initialBudget',
      'initialDemand',
      'dataMined',
      'finalPayment',
      'ftfAmountOriginalLoss',
      'ftfAmountRecovered',
      'contributionFromOtherInsurance',
    ];

    // Define which fields are integer fields (32-bit signed integer limit)
    const integerFields = [
      'lengthOfDisruption',
      'recordsImpacted',
      'daysDetectionToContainment',
      'numberOfEndpoints',
      'daysOccurrenceToDetection',
      'daysDetectionToForensicCompletion',
      'becAccountsCompromised',
      'dataMined',
      'initialBudget',
      'initialDemand',
      'finalPayment',
      'ftfAmountOriginalLoss',
      'ftfAmountRecovered',
      'contributionFromOtherInsurance',
    ];

    // Define which fields are dates
    const dateFields = ['rorSentDate', 'polSentDate'];

    // Use the same MAX_INT_VALUE constant defined at component level

    const dataToSave = Object.entries(filteredDirtyFields).reduce(
      (acc, [fieldName, fieldData]) => {
        let value: string | boolean | number | null | string[] =
          fieldData.value;

        if (numberFields.includes(fieldName) && typeof value === 'string') {
          // Remove commas and dollar signs, then convert to number
          const cleanedValue = value.replace(/[$,]/g, '');
          if (cleanedValue === '') {
            value = null; // Empty string should be null for number fields
          } else {
            if (integerFields.includes(fieldName)) {
              const numericValue = parseInt(cleanedValue, 10);

              if (isNaN(numericValue)) {
                value = null;
              } else {
                // Validate against GraphQL Int limit
                if (numericValue > MAX_INT_VALUE) {
                  notify(
                    `${fieldName} value ${numericValue} exceeds maximum allowed value of ${MAX_INT_VALUE.toLocaleString()}`,
                    {
                      variant: 'error',
                    }
                  );
                  return acc;
                }
                value = numericValue;
              }
            } else {
              const numericValue = parseFloat(cleanedValue);
              if (isNaN(numericValue)) {
                value = null;
              } else {
                value = numericValue;
              }
            }
          }
        }

        // Convert date fields to ISO format or null if empty
        if (dateFields.includes(fieldName)) {
          if (typeof value === 'string') {
            if (value === '' || value.trim() === '') {
              value = null; // Empty string should be null for date fields
            } else {
              try {
                // Try to parse the date and convert to ISO format
                const date = new Date(value);
                if (!isNaN(date.getTime())) {
                  value = date.toISOString();
                } else {
                  value = null; // Invalid date
                }
              } catch (error) {
                console.error('Error parsing date:', value, error);
              }
            }
          }
        }

        // Convert empty string to null for threatActorUuid
        if (fieldName === 'threatActorUuid' && (value === '' || !value)) {
          value = null;
        }

        // Serialize typePaid array to comma-separated string; null if empty
        if (fieldName === 'typePaid') {
          if (Array.isArray(value)) {
            value = value.length === 0 ? null : value.join(',');
          }
        }

        acc[fieldName] = value;
        return acc;
      },
      {} as Record<string, string | boolean | number | null | string[]>
    );

    const formData = dataToSave;
    return formData;
  }, [dirtyFields, notify]);

  const saveChangesToBackend = useCallback(async () => {
    // Capture the dirty fields at the start of save operation
    const fieldsBeingSaved = { ...dirtyFields };
    const updatedData = prepareDataForBackend();

    if (!updatedData) {
      return;
    }

    try {
      notify('Saving...', {
        variant: 'info',
      });
      await upsertClaimData({
        claimInput: {
          claimUuid: claimUuid,
          ...updatedData,
        },
      });

      // Don't update form fields during auto-save to prevent overwriting user input
      // The form will get fresh data when the page refreshes or user navigates away and back

      // Only clear the fields that were actually saved
      setDirtyFields((prev) => {
        const remaining = { ...prev };
        Object.keys(fieldsBeingSaved).forEach((fieldName) => {
          delete remaining[fieldName];
        });
        return remaining;
      });

      // Refetch the latest data from the server
      await refetchClaimData();

      notify('Claim data has been updated', {
        variant: 'success',
      });
    } catch (error) {
      console.error('Error saving changes:', error);
      notify(`${error}`, {
        variant: 'error',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    prepareDataForBackend,
    notify,
    upsertClaimData,
    claimUuid,
    refetchClaimData,
    form,
  ]);
  const handleCatCodeSelection = useCallback(
    async (catCodeUuid: string | null) => {
      try {
        notify('Saving...', {
          variant: 'info',
        });
        await upsertClaimData({
          claimInput: {
            claimUuid: claimUuid,
            catCodeUuid: catCodeUuid,
          },
        });

        // Refetch the latest data from the server
        await refetchClaimData();

        notify('Catastrophe code updated', {
          variant: 'success',
        });
      } catch (error) {
        console.error('Error saving changes:', error);
        notify(`${error}`, {
          variant: 'error',
        });
      }
    },
    [claimUuid, notify, refetchClaimData, upsertClaimData]
  );

  // Auto-save changes every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      // Filter out CAT code fields from dirtyFields to prevent them from being auto-saved with waiting time
      const filteredDirtyFields = Object.keys(dirtyFields).filter(
        (key) => key !== 'catCodeUuid'
      );

      if (
        filteredDirtyFields.length > 0 &&
        !dateFieldErrors.polSentDate &&
        !dateFieldErrors.rorSentDate
      ) {
        saveChangesToBackend();
      }
    }, 3 * 1000);

    return () => clearInterval(interval);
  }, [
    saveChangesToBackend,
    dirtyFields,
    dateFieldErrors.polSentDate,
    dateFieldErrors.rorSentDate,
  ]);

  const [expandedAccordions, setExpandedAccordions] = useState<
    Record<string, boolean>
  >({
    claimData: true,
    catCodes: true,
  });

  const handleAccordionChange =
    (accordionKey: string) =>
    (_event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpandedAccordions((prev) => ({
        ...prev,
        [accordionKey]: newExpanded,
      }));
    };

  return (
    <Box className="claim-data">
      <Paper elevation={0}>
        <FormProvider {...form}>
          <ConvergeDetailAccordion
            expanded={expandedAccordions['claimData']}
            onChange={handleAccordionChange('claimData')}
            title="CLAIM DATA"
            description="Include information applicable to this claim. Fields may be left blank if they do not apply"
            detailsClassName="accordion-details-style"
            paddingBottom="30px"
            testId="claim-data-accordion"
          >
            {claimDataLoading ? (
              <CircularLoader className="circular-loader-style" />
            ) : (
              <Box className="data-container">
                <Box className="row-data">
                  <Box width={'100%'}>
                    <NewTypeAhead
                      minWidthDropdown="200px"
                      name={'threatActorUuid'}
                      options={threatActorsOption || []}
                      value={form.watch('threatActorUuid')}
                      placeholder="Select Threat Actor"
                      dataTestId="threat-actor"
                      label={'THREAT ACTOR'}
                    />
                  </Box>
                  <Box width={'100%'}>
                    <Controller
                      name="cryptoWalletId"
                      control={form.control}
                      render={({ field }) => (
                        <NewFormTextField
                          {...field}
                          label="WALLET ID"
                          placeholder="Enter Wallet ID"
                          data-testid="claim-walletId"
                          onBlur={(e) => {
                            field.onBlur();
                            handleFieldChange(
                              'cryptoWalletId',
                              e?.target?.value || null
                            );
                          }}
                        />
                      )}
                    />
                  </Box>
                  <Box width={'100%'}>
                    <Controller
                      name="lengthOfDisruption"
                      control={form.control}
                      render={({ field }) => (
                        <NewFormTextField
                          {...field}
                          label="DISRUPTION LENGTH(IN DAYS)"
                          placeholder="Enter Number of Days"
                          data-testid="claim-disruptionLength"
                          type="number"
                          inputProps={{ min: 0, max: 2147483647 }}
                          onKeyDown={handleIntegerKeyDown}
                          onBlur={(e) => {
                            field.onBlur();
                            handleFieldChange(
                              'lengthOfDisruption',
                              e?.target?.value || ''
                            );
                          }}
                        />
                      )}
                    />
                  </Box>
                </Box>
                <Box className="row-data">
                  <Box width={'100%'}>
                    <Controller
                      name="claimVariant"
                      control={form.control}
                      render={({ field }) => (
                        <NewFormTextField
                          {...field}
                          label="VARIANT"
                          placeholder="Enter Variant"
                          data-testid="claim-variant"
                          onBlur={(e) => {
                            field.onBlur();
                            handleFieldChange(
                              'claimVariant',
                              e?.target?.value || null
                            );
                          }}
                        />
                      )}
                    />
                  </Box>

                  <Box width={'100%'}>
                    <ClaimFormNumberInputAdornmentField
                      name="initialBudget"
                      adornmentText="$"
                      label="INITIAL BUDGET"
                      placeholder="0"
                      paddingLeft="10px"
                      onBlur={(e) => {
                        handleFieldChange(
                          'initialBudget',
                          String(e?.target?.value)
                        );
                      }}
                    />
                  </Box>
                  <Box width={'100%'}>
                    <Controller
                      name="daysOccurrenceToDetection"
                      control={form.control}
                      render={({ field }) => (
                        <NewFormTextField
                          {...field}
                          label="OCCURRENCE TO DETECTION"
                          placeholder="Enter Number of Days"
                          data-testid="claim-occurrence-to-detection"
                          type="number"
                          inputProps={{ min: 0, max: 2147483647 }}
                          onKeyDown={handleIntegerKeyDown}
                          onBlur={(e) => {
                            field.onBlur();
                            handleFieldChange(
                              'daysOccurrenceToDetection',
                              e?.target?.value || ''
                            );
                          }}
                        />
                      )}
                    />
                  </Box>
                </Box>
                <Box className="row-data2">
                  <Box width={'100%'}>
                    <Controller
                      name="numberOfEndpoints"
                      control={form.control}
                      render={({ field }) => (
                        <NewFormTextField
                          {...field}
                          label="NUMBER OF ENDPOINTS"
                          placeholder="Enter Number of Endpoints"
                          data-testid="claim-endpoints"
                          type="number"
                          name="numberOfEndpoints"
                          inputProps={{ min: 0 }}
                          onKeyDown={handleIntegerKeyDown}
                          onBlur={(e) => {
                            field.onBlur();
                            handleFieldChange(
                              'numberOfEndpoints',
                              e?.target?.value || ''
                            );
                          }}
                        />
                      )}
                    />
                  </Box>

                  <Box width={'100%'}>
                    <Box className="claim-radio">Ransom </Box>
                    <Controller
                      name="typePaid"
                      control={form.control}
                      render={({ field }) => {
                        const values: string[] = Array.isArray(field.value)
                          ? field.value
                          : [];
                        const toggle = (option: string) => {
                          const next = values.includes(option)
                            ? values.filter((v) => v !== option)
                            : [...values, option];
                          field.onChange(next);
                          handleFieldChange('typePaid', next);
                        };
                        return (
                          <>
                            <FormCheckBoxField
                              label={'For Decryption'}
                              checked={values.includes('Decryption')}
                              handleChange={() => toggle('Decryption')}
                              labelPlacement="end"
                              size="small"
                              name={'typePaid-decryption'}
                            />
                            <FormCheckBoxField
                              label={'Data Suppression'}
                              checked={values.includes('Data Suppression')}
                              handleChange={() => toggle('Data Suppression')}
                              labelPlacement="end"
                              size="small"
                              name={'typePaid-data-suppression'}
                            />
                          </>
                        );
                      }}
                    />
                  </Box>
                  <Box width={'100%'}>
                    <Controller
                      name="daysDetectionToContainment"
                      control={form.control}
                      render={({ field }) => (
                        <NewFormTextField
                          {...field}
                          label="DETECTION TO CONTAINMENT"
                          placeholder="Enter Number of Days"
                          data-testid="claim-detection-to-containment"
                          name="daysDetectionToContainment"
                          type="number"
                          inputProps={{ min: 0, max: 2147483647 }}
                          onKeyDown={handleIntegerKeyDown}
                          onBlur={(e) => {
                            field.onBlur();
                            handleFieldChange(
                              'daysDetectionToContainment',
                              e?.target?.value || ''
                            );
                          }}
                        />
                      )}
                    />
                  </Box>
                </Box>
                <Box className="row-data">
                  <Box width={'100%'}>
                    <Controller
                      name="recordsImpacted"
                      control={form.control}
                      render={({ field }) => (
                        <NewFormTextField
                          {...field}
                          type="number"
                          label="RECORDS IMPACTED"
                          placeholder="Enter Number Impacted"
                          data-testid="claim-records-impacted"
                          inputProps={{ min: 0 }}
                          onKeyDown={handleIntegerKeyDown}
                          onBlur={(e) => {
                            field.onBlur();
                            handleFieldChange(
                              'recordsImpacted',
                              e?.target?.value || ''
                            );
                          }}
                        />
                      )}
                    />
                  </Box>
                  <Box width={'100%'}>
                    <ClaimFormNumberInputAdornmentField
                      name="initialDemand"
                      adornmentText="$"
                      label="INITIAL DEMAND"
                      placeholder="0"
                      paddingLeft="10px"
                      onBlur={(e) => {
                        handleFieldChange(
                          'initialDemand',
                          String(e?.target?.value)
                        );
                      }}
                    />
                  </Box>
                  <Box width={'100%'}>
                    <Controller
                      name="daysDetectionToForensicCompletion"
                      control={form.control}
                      render={({ field }) => (
                        <NewFormTextField
                          {...field}
                          label="CONTAINMENT TO FORENSIC ANALYSIS"
                          placeholder="Enter Number of Days"
                          data-testid="claim-containmentForensicAnalysis"
                          type="number"
                          name="daysDetectionToForensicCompletion"
                          inputProps={{ min: 0 }}
                          onKeyDown={handleIntegerKeyDown}
                          onBlur={(e) => {
                            field.onBlur();
                            handleFieldChange(
                              'daysDetectionToForensicCompletion',
                              e?.target?.value || ''
                            );
                          }}
                        />
                      )}
                    />
                  </Box>
                </Box>
                <Box className="row-data2">
                  <Box width={'100%'}>
                    <Box className="claim-radio" style={{ marginTop: '5px' }}>
                      DATA LOSS{' '}
                    </Box>

                    <Controller
                      name="dataLoss"
                      control={form.control}
                      render={({ field }) => {
                        const handleRadioClick = (value: string) => {
                          const newValue = value === field.value ? null : value;
                          field.onChange(newValue);
                          handleFieldChange('dataLoss', newValue);
                        };

                        return (
                          <RadioGroup {...field} value={field.value || null}>
                            <FormControlLabel
                              value="ReportWith24hrs"
                              control={
                                <Radio
                                  className="radio-button unchecked"
                                  classes={{ checked: 'checked' }}
                                  onClick={() =>
                                    handleRadioClick('ReportWith24hrs')
                                  }
                                  sx={{
                                    '&.Mui-disabled': {
                                      opacity: 0.4,
                                    },
                                  }}
                                />
                              }
                              label={'Reported Within 24 Hours'}
                            />

                            <FormControlLabel
                              value={'ReportWith48hrs'}
                              control={
                                <Radio
                                  className="radio-button unchecked"
                                  classes={{ checked: 'checked' }}
                                  onClick={() =>
                                    handleRadioClick('ReportWith48hrs')
                                  }
                                  sx={{
                                    '&.Mui-disabled': {
                                      opacity: 0.4,
                                    },
                                  }}
                                />
                              }
                              label={'Reported Within 48 Hours'}
                            />
                            <FormControlLabel
                              value={'other'}
                              control={
                                <Radio
                                  className="radio-button unchecked"
                                  classes={{ checked: 'checked' }}
                                  onClick={() => handleRadioClick('other')}
                                  sx={{
                                    '&.Mui-disabled': {
                                      opacity: 0.4,
                                    },
                                  }}
                                />
                              }
                              label={'Other'}
                            />
                          </RadioGroup>
                        );
                      }}
                    />
                  </Box>
                  <Box width={'100%'}>
                    <ClaimFormNumberInputAdornmentField
                      name="finalPayment"
                      adornmentText="$"
                      label="FINAL PAYMENT"
                      placeholder="0"
                      paddingLeft="10px"
                      onBlur={(e) => {
                        handleFieldChange(
                          'finalPayment',
                          String(e?.target?.value)
                        );
                      }}
                    />
                  </Box>
                  <Box width={'100%'}></Box>
                </Box>
                <Box className="row-data">
                  <Box width={'100%'}>
                    <Controller
                      name="dataMined"
                      control={form.control}
                      render={({ field }) => (
                        <NewFormTextField
                          {...field}
                          label="DATA MINED"
                          placeholder="Enter Amount"
                          data-testid="claim-dataMined"
                          name="dataMined"
                          type="number"
                          inputProps={{ min: 0, max: 2147483647 }}
                          onKeyDown={handleIntegerKeyDown}
                          onBlur={(e) => {
                            field.onBlur();
                            handleFieldChange(
                              'dataMined',
                              e?.target?.value || null
                            );
                          }}
                        />
                      )}
                    />
                  </Box>
                  <Box width={'100%'}>
                    <ClaimFormNumberInputAdornmentField
                      name="ftfAmountOriginalLoss"
                      adornmentText="$"
                      label="FTF AMOUNT OF ORIGINAL LOSS"
                      placeholder="0"
                      paddingLeft="10px"
                      onBlur={(e) => {
                        handleFieldChange(
                          'ftfAmountOriginalLoss',
                          String(e?.target?.value)
                        );
                      }}
                    />
                  </Box>
                  <Box width={'100%'}></Box>
                </Box>
                <Box className="row-data">
                  <Box width={'100%'}>
                    <Box width={'100%'}>
                      <Controller
                        name="becAccountsCompromised"
                        control={form.control}
                        render={({ field }) => (
                          <NewFormTextField
                            {...field}
                            label="BEC ACCOUNTS COMPROMISED"
                            placeholder="Enter Number of Accounts"
                            data-testid="claim-becAccountsCompromised"
                            type="number"
                            name="becAccountsCompromised"
                            inputProps={{ min: 0 }}
                            onKeyDown={handleIntegerKeyDown}
                            onBlur={(e) => {
                              field.onBlur();
                              handleFieldChange(
                                'becAccountsCompromised',
                                e?.target?.value || ''
                              );
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                  <Box width={'100%'}>
                    <ClaimFormNumberInputAdornmentField
                      name="ftfAmountRecovered"
                      adornmentText="$"
                      label="FTF AMOUNT RECOVERED"
                      placeholder="0"
                      paddingLeft="10px"
                      onBlur={(e) => {
                        handleFieldChange(
                          'ftfAmountRecovered',
                          String(e?.target?.value)
                        );
                      }}
                    />
                  </Box>
                  <Box width={'100%'}></Box>
                </Box>
                <Box className="row-data">
                  <Box width={'100%'}></Box>
                  <Box width={'100%'}>
                    <ClaimFormNumberInputAdornmentField
                      name="contributionFromOtherInsurance"
                      adornmentText="$"
                      label="CONTRIBUTION FROM OTHER INSURANCE"
                      placeholder="0"
                      paddingLeft="10px"
                      onBlur={(e) => {
                        handleFieldChange(
                          'contributionFromOtherInsurance',
                          String(e?.target?.value)
                        );
                      }}
                    />
                  </Box>
                  <Box width={'100%'}></Box>
                </Box>
              </Box>
            )}
          </ConvergeDetailAccordion>
        </FormProvider>

        <ConvergeDetailAccordion
          expanded={expandedAccordions['catCodes']}
          onChange={handleAccordionChange('catCodes')}
          title="CAT CODES"
          description="The catastrophe code associated with this claim."
          detailsClassName="accordion-details-style"
          paddingBottom="30px"
          testId="cat-codes-accordion"
        >
          <Box className="cat-container">
            <NewTypeAhead
              minWidthDropdown="200px"
              name={'CatCodeUuid'}
              options={catCodeOption || []}
              value={form.watch('catCodeUuid')}
              isLoading={catCodesLoading}
              placeholder="Select Catastrophe Code"
              dataTestId="Cat-Code"
              label={'CAT CODE'}
              dropdownOptionWidth={true}
              onChangeOption={(option) => {
                if (option && option.value) {
                  form.setValue('catCodeUuid', option.value);
                  handleCatCodeSelection(option.value);
                } else {
                  form.setValue('catCodeUuid', '');
                  handleCatCodeSelection(null);
                }
              }}
            />
          </Box>
        </ConvergeDetailAccordion>
      </Paper>
    </Box>
  );
};
